import React, { useContext, useState } from 'react';
import styled, { css } from 'styled-components';
import {
  AlertNote,
  GetColor,
  Icon,
  Tooltip,
  TooltipPosition,
  ZIndex,
  ColorUtils,
  ExternalActivityListener,
  portalMenuIgnoreActivityClassName,
} from 'venn-ui-kit';
import { PortfolioLabContext, ConfirmationModal, UnsavedChangesTrigger } from 'venn-components';
import AllocationConstraints from './AllocationConstraints';
import FactorExposureConstraints from './FactorExposureConstraints';
import BenchmarkConstraints from './BenchmarkConstraints';
import type { LabSideBarProps } from './LabSideBar';

export const SIDE_BAR_WIDTH = 56;
const CONSTRAINT_ROW_HEIGHT = 40;
const TOP_BAR_HEIGHT = 36;
const PANEL_WIDTH = 430;

const LabSideBarNew = ({
  isSideBarCollapsed,
  toggleSideBarCollapsed,
  allocationSectionExpanded,
  toggleAllocationSection,
  factorSectionExpanded,
  toggleFactorSection,
  benchmarkSectionExpanded,
  toggleBenchmarkSection,
  optimizing,
}: LabSideBarProps) => {
  const { loadingPolicy, allocationConstraints, factorConstraints, objective } = useContext(PortfolioLabContext);
  const [isMaximized, setIsMaximized] = useState(true);
  const [isAllocationInEditState, setIsAllocationInEditState] = useState(false);
  const [isFactorExposureInEditState, setIsFactorExposureInEditState] = useState(false);
  const [isBenchmarkInEditState, setIsBenchmarkInEditState] = useState(false);

  const isMaximizeSharpe = objective === 'maximizeSharpe';

  const onToggleAllocationIcon = () => {
    if (isSideBarCollapsed) {
      if (!allocationSectionExpanded) {
        toggleAllocationSection();
      }
      if (factorSectionExpanded) {
        toggleFactorSection();
      }
      if (benchmarkSectionExpanded) {
        toggleBenchmarkSection();
      }
      toggleSideBarCollapsed();
    } else {
      toggleAllocationSection();
    }
  };

  const onToggleFactorIcon = () => {
    if (isSideBarCollapsed) {
      if (!factorSectionExpanded) {
        toggleFactorSection();
      }
      if (allocationSectionExpanded) {
        toggleAllocationSection();
      }
      if (benchmarkSectionExpanded) {
        toggleBenchmarkSection();
      }
      toggleSideBarCollapsed();
    } else {
      toggleFactorSection();
    }
  };

  const onToggleBenchmarkIcon = () => {
    if (isSideBarCollapsed) {
      if (!benchmarkSectionExpanded) {
        toggleBenchmarkSection();
      }
      if (allocationSectionExpanded) {
        toggleAllocationSection();
      }
      if (factorSectionExpanded) {
        toggleFactorSection();
      }
      toggleSideBarCollapsed();
    } else {
      toggleBenchmarkSection();
    }
  };

  const allocationConstraintsCount = loadingPolicy ? 0 : (allocationConstraints?.length ?? 0);
  const factorConstraintsCount = loadingPolicy ? 0 : (factorConstraints?.length ?? 0);

  const isInEditState = isAllocationInEditState || isFactorExposureInEditState || isBenchmarkInEditState;

  return (
    <UnsavedChangesTrigger isNotSaved={isInEditState}>
      {({ isOpen, handleConfirmNavigation, setIsOpen, handleReject }) => (
        <>
          {isOpen ? (
            <ConfirmationModal
              header="You have not saved your constraint changes."
              subhead="Would you like to exit without saving? "
              proceedLabel="Yes, discard changes"
              className="unsaved-changes-modal"
              onCancel={() => handleReject()}
              onSecondaryClick={() => handleReject()}
              onProceed={() => {
                handleConfirmNavigation();
                toggleSideBarCollapsed();
                setIsAllocationInEditState(false);
                setIsFactorExposureInEditState(false);
                setIsBenchmarkInEditState(false);
              }}
            />
          ) : null}
          <ExternalActivityListener
            onExternalActivity={isInEditState ? () => setIsOpen(true) : toggleSideBarCollapsed}
            listeningEnabled={!isSideBarCollapsed && !optimizing}
            ignoreActivityFromClassName={[
              portalMenuIgnoreActivityClassName,
              'update-constraints-modal',
              'clear-all-constraints-modal',
              'unsaved-changes-modal',
              'qa-navigate-to-home',
              'side-navbar',
            ]}
          >
            <SideBarAndPanelContainer isSideBarCollapsed={isSideBarCollapsed} isMaximized={isMaximized}>
              <SideBarLine>
                <ConstraintsLabel>Constraints</ConstraintsLabel>
                {!isSideBarCollapsed && (
                  <InputsBar>
                    <PanelButtons>
                      <PanelButton type="button" onClick={() => setIsMaximized(!isMaximized)}>
                        <Icon type={isMaximized ? 'arrow-left-to-line' : 'arrow-right-to-line'} />
                      </PanelButton>
                      <PanelButton
                        type="button"
                        onClick={isInEditState ? () => setIsOpen(true) : toggleSideBarCollapsed}
                      >
                        <Icon type="times" />
                      </PanelButton>
                    </PanelButtons>
                  </InputsBar>
                )}
              </SideBarLine>
              <InputSections>
                <SideBarLine isConstraintRow={!isSideBarCollapsed} isRowExpanded={allocationSectionExpanded}>
                  <InputSectionIcon
                    type="button"
                    onClick={onToggleAllocationIcon}
                    borderRight={!isSideBarCollapsed}
                    isExpanded={!isSideBarCollapsed && allocationSectionExpanded}
                    className="qa-allocation-constraints-btn"
                  >
                    <Icon type="sliders-h" />
                    Allocation
                  </InputSectionIcon>
                  {!isSideBarCollapsed && (
                    <InputSectionExpand
                      type="button"
                      onClick={toggleAllocationSection}
                      isExpanded={allocationSectionExpanded}
                    >
                      <div>Allocation Constraints ({allocationConstraintsCount})</div>
                      <ExpandIcon type={allocationSectionExpanded ? 'chevron-up' : 'chevron-down'} />
                    </InputSectionExpand>
                  )}
                </SideBarLine>
                {!isSideBarCollapsed && allocationSectionExpanded && (
                  <SideBarLine>
                    <InputSection isExpanded>
                      <InputSectionContent>
                        <AllocationConstraints
                          isInEditState={isAllocationInEditState}
                          setIsInEditState={setIsAllocationInEditState}
                          isSideBarMaximized={isMaximized}
                        />
                      </InputSectionContent>
                    </InputSection>
                  </SideBarLine>
                )}
                <SideBarLine isConstraintRow={!isSideBarCollapsed} isRowExpanded={factorSectionExpanded}>
                  <InputSectionIcon
                    type="button"
                    onClick={onToggleFactorIcon}
                    borderRight={!isSideBarCollapsed}
                    isExpanded={!isSideBarCollapsed && factorSectionExpanded}
                    className="qa-exposure-btn"
                  >
                    <Icon type="dna" />
                    Exposure
                  </InputSectionIcon>
                  {!isSideBarCollapsed && (
                    <InputSectionExpand type="button" onClick={toggleFactorSection} isExpanded={factorSectionExpanded}>
                      <div>Factor Exposure Constraints ({factorConstraintsCount})</div>
                      <ExpandIcon type={factorSectionExpanded ? 'chevron-up' : 'chevron-down'} />
                    </InputSectionExpand>
                  )}
                </SideBarLine>
                {!isSideBarCollapsed && factorSectionExpanded && (
                  <SideBarLine>
                    <InputSection isExpanded>
                      <InputSectionContent>
                        <FactorExposureConstraints
                          isInEditState={isFactorExposureInEditState}
                          setIsInEditState={setIsFactorExposureInEditState}
                          isSideBarMaximized={isMaximized}
                        />
                      </InputSectionContent>
                    </InputSection>
                  </SideBarLine>
                )}
                <SideBarLine
                  isConstraintRow={!isSideBarCollapsed && !isMaximizeSharpe}
                  isRowExpanded={benchmarkSectionExpanded}
                >
                  <Tooltip
                    content={
                      isMaximizeSharpe && isSideBarCollapsed
                        ? 'Benchmark Relative Constraint is not available when maximizing sharpe.'
                        : undefined
                    }
                    position={TooltipPosition.Right}
                    usePortal
                  >
                    <InputSectionIcon
                      type="button"
                      onClick={isMaximizeSharpe ? undefined : onToggleBenchmarkIcon}
                      borderRight={!isSideBarCollapsed}
                      isExpanded={!isSideBarCollapsed && benchmarkSectionExpanded && !isMaximizeSharpe}
                      $isDisabled={isMaximizeSharpe}
                      aria-disabled={isMaximizeSharpe}
                      className="qa-benchmark-constraints-btn"
                    >
                      <Icon type="arrows-left-right" />
                      Benchmark
                    </InputSectionIcon>
                  </Tooltip>
                  {!isSideBarCollapsed &&
                    (isMaximizeSharpe ? (
                      <DisabledSectionExpand>Benchmark Relative Constraint</DisabledSectionExpand>
                    ) : (
                      <InputSectionExpand
                        type="button"
                        onClick={toggleBenchmarkSection}
                        isExpanded={benchmarkSectionExpanded}
                      >
                        <div>Benchmark Relative Constraint</div>
                        <ExpandIcon type={benchmarkSectionExpanded ? 'chevron-up' : 'chevron-down'} />
                      </InputSectionExpand>
                    ))}
                </SideBarLine>
                {!isSideBarCollapsed && (isMaximizeSharpe || benchmarkSectionExpanded) && (
                  <SideBarLine hideBorder={isMaximizeSharpe}>
                    <InputSection isExpanded>
                      {isMaximizeSharpe ? (
                        <AlertNote size="small">
                          Benchmark Relative Constraint is not available when maximizing sharpe.
                        </AlertNote>
                      ) : (
                        <InputSectionContent>
                          <BenchmarkConstraints
                            isInEditState={isBenchmarkInEditState}
                            setIsInEditState={setIsBenchmarkInEditState}
                            isSideBarMaximized={isMaximized}
                          />
                        </InputSectionContent>
                      )}
                    </InputSection>
                  </SideBarLine>
                )}
              </InputSections>
            </SideBarAndPanelContainer>
          </ExternalActivityListener>
        </>
      )}
    </UnsavedChangesTrigger>
  );
};

export default LabSideBarNew;

const SideBarAndPanelContainer = styled.div<{ isSideBarCollapsed?: boolean; isMaximized?: boolean }>`
  position: absolute;
  height: 100%;
  width: ${({ isSideBarCollapsed, isMaximized }) =>
    isSideBarCollapsed
      ? SIDE_BAR_WIDTH
      : isMaximized
        ? SIDE_BAR_WIDTH + PANEL_WIDTH * 2
        : SIDE_BAR_WIDTH + PANEL_WIDTH}px;
  top: -1px;
  left: 0;

  border-top: 1px solid ${GetColor.GreyScale.Grey40};
  border-right: 1px solid ${GetColor.GreyScale.Grey40};
  ${({ isSideBarCollapsed }) => !isSideBarCollapsed && 'box-shadow: 10px 0px 5px rgba(0, 0, 0, 0.1);'}
  z-index: ${ZIndex.Cover};

  @media print {
    display: none;
  }
`;

const InputSectionExpand = styled.button<{ isExpanded?: boolean }>`
  width: calc(100% - ${SIDE_BAR_WIDTH}px);
  font-size: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-left: 10px;
  font-weight: bold;
  ${({ isExpanded }) =>
    isExpanded
      ? css`
          background-color: ${GetColor.Primary.Dark};
          color: ${GetColor.White};
          &:hover {
            color: ${GetColor.White};
          }
        `
      : css`
          background-color: ${GetColor.White};
          color: ${GetColor.Black};
          &:hover {
            color: ${GetColor.Black};
          }
        `}
`;

const InputSectionIcon = styled.button<{
  borderTop?: boolean;
  borderRight?: boolean;
  $isDisabled?: boolean;
  isExpanded?: boolean;
}>`
  width: ${SIDE_BAR_WIDTH}px;
  height: ${CONSTRAINT_ROW_HEIGHT}px;
  ${({ borderRight }) =>
    borderRight &&
    css`
      position: relative;
      &::after {
        content: '';
        position: absolute;
        right: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 1px;
        height: 24px;
        background-color: ${GetColor.GreyScale.Grey30};
      }
    `}
  ${({ borderTop }) =>
    borderTop &&
    css`
      border-top: 1px solid ${GetColor.GreyScale.Grey40};
    `}
  ${({ isExpanded }) =>
    isExpanded
      ? css`
          background-color: ${GetColor.Primary.Dark};
          color: ${GetColor.White};
          &:hover {
            color: ${GetColor.White};
          }
        `
      : css`
          background-color: ${GetColor.White};
          color: ${GetColor.Primary.Dark};
          &:hover {
            color: ${GetColor.Primary.Dark};
          }
        `}
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-family: 'Roboto Condensed', 'sans-serif';
  font-weight: bold;
  i {
    font-size: 14px;
    margin-bottom: 2px;
  }
  padding-top: 2px;
  ${({ borderRight, isExpanded }) =>
    !borderRight &&
    !isExpanded &&
    css`
      &:hover {
        background-color: ${GetColor.GreyScale.Grey10};
      }
    `}
  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      cursor: not-allowed;
      opacity: 0.6;
      color: ${GetColor.GreyScale.Grey60};
      &:hover {
        color: ${GetColor.GreyScale.Grey60};
      }
    `}
`;

const SideBarLine = styled.div<{ hideBorder?: boolean; isConstraintRow?: boolean; isRowExpanded?: boolean }>`
  display: flex;
  flex: ${SIDE_BAR_WIDTH - 1}px 1;
  ${({ hideBorder }) =>
    !hideBorder &&
    css`
      border-bottom: 1px solid ${GetColor.GreyScale.Grey40};
    `}
  ${({ isConstraintRow, isRowExpanded }) =>
    isConstraintRow &&
    !isRowExpanded &&
    css`
      &:hover {
        ${InputSectionIcon}, ${InputSectionExpand} {
          background-color: ${GetColor.GreyScale.Grey10};
        }
      }
    `}
`;

const ConstraintsLabel = styled.div`
  width: ${SIDE_BAR_WIDTH}px;
  height: ${TOP_BAR_HEIGHT}px;
  background-color: ${GetColor.GreyScale.Grey10};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Roboto Condensed', 'sans-serif';
  font-weight: bold;
  font-size: 10px;
`;

const InputsBar = styled.div`
  background-color: ${GetColor.GreyScale.Grey10};
  height: ${TOP_BAR_HEIGHT}px;
  width: calc(100% - ${SIDE_BAR_WIDTH}px);
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 8px;
`;

const PanelButtons = styled.div`
  display: flex;
  gap: 4px;
`;

const PanelButton = styled.button`
  width: 24px;
  height: 20px;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${GetColor.GreyScale.Grey30};
  border-radius: 4px;
  padding: 6px;
  background-color: ${GetColor.White};
  color: ${GetColor.DarkBlue};
  &:hover {
    background-color: ${ColorUtils.opacifyFrom(GetColor.DarkBlue, 0.1)};
  }
`;

const InputSections = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  height: calc(100% - ${TOP_BAR_HEIGHT}px);
  background-color: ${GetColor.White};
`;

const InputSection = styled.div<{ isExpanded?: boolean }>`
  width: 100%;
  background-color: ${({ isExpanded }) => (isExpanded ? GetColor.GreyScale.Grey10 : GetColor.White)};
`;

const ExpandIcon = styled(Icon)`
  width: 24px;
  margin-right: 8px;
  text-align: center;
`;

const DisabledSectionExpand = styled.div`
  width: calc(100% - ${SIDE_BAR_WIDTH}px);
  height: ${CONSTRAINT_ROW_HEIGHT}px;
  background-color: ${GetColor.White};
  font-size: 12px;
  display: flex;
  align-items: center;
  padding-left: 10px;
  font-weight: bold;
  color: ${GetColor.GreyScale.Grey60};
  white-space: nowrap;
`;

const InputSectionContent = styled.div`
  width: 100%;
  min-height: 296px;
  display: flex;
  flex-direction: column;
`;
